import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fileUrl = (path) => {
  const token = localStorage.getItem("ipsita_token");
  return `${process.env.REACT_APP_BACKEND_URL}/api/enquiry-files/${path}?auth=${encodeURIComponent(token || "")}`;
};

const STATUSES = ["NEW", "CONTACTED", "DISCUSSING", "CONFIRMED", "COMPLETED", "DECLINED"];

const STATUS_COLORS = {
  NEW: "bg-alta/10 text-alta border-alta/30",
  CONTACTED: "bg-mustard/15 text-mustard border-mustard/30",
  DISCUSSING: "bg-bindigo/10 text-bindigo border-bindigo/30",
  CONFIRMED: "bg-forest/10 text-forest border-forest/30",
  COMPLETED: "bg-ink/10 text-ink border-ink/20",
  DECLINED: "bg-ink/5 text-ink/40 border-ink/10",
};

export default function EnquiriesAdmin() {
  const [enquiries, setEnquiries] = useState([]);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("ALL");

  const load = useCallback(() => {
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => toast.error("Could not load enquiries"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((s) => s.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch {
      toast.error("Status update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setEnquiries((s) => s.filter((e) => e.id !== id));
      setOpen(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  const visible = filter === "ALL" ? enquiries : enquiries.filter((e) => e.status === filter);

  return (
    <div data-testid="enquiries-admin">
      <h1 className="font-serif text-4xl font-light text-ink">Enquiries</h1>
      <p className="mt-2 text-sm font-light text-ink/55">Every conversation that began on the site.</p>

      <div className="mt-8 flex flex-wrap gap-2">
        {["ALL", ...STATUSES].map((s) => (
          <button
            key={s}
            data-testid={`enquiry-filter-${s.toLowerCase()}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 text-[10px] tracking-[0.18em] uppercase border transition-colors ${
              filter === s ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/55 hover:border-ink"
            }`}
          >
            {s} {s === "ALL" ? `(${enquiries.length})` : `(${enquiries.filter((e) => e.status === s).length})`}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {visible.length === 0 && <p className="text-ink/50 font-light text-sm py-6" data-testid="enquiries-empty">No enquiries in this view.</p>}
        {visible.map((e) => (
          <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="bg-paper border border-ink/10">
            <button
              onClick={() => setOpen(open?.id === e.id ? null : e)}
              data-testid={`enquiry-open-${e.id}`}
              className="w-full p-5 flex flex-wrap items-center gap-4 justify-between text-left"
            >
              <div>
                <p className="font-medium text-ink text-sm">{e.name} <span className="font-light text-ink/50">· {e.enquiry_type}</span></p>
                <p className="text-xs font-light text-ink/55 mt-1">{e.email}{e.city ? ` · ${e.city}` : ""} · {new Date(e.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 border ${STATUS_COLORS[e.status] || STATUS_COLORS.NEW}`}>{e.status}</span>
            </button>

            <AnimatePresence>
              {open?.id === e.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-6 border-t border-ink/10 pt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 text-sm font-light text-ink/70">
                      <p><span className="text-ink/40 text-[10px] tracking-[0.2em] uppercase block">Phone</span>{e.phone || "—"}</p>
                      <p><span className="text-ink/40 text-[10px] tracking-[0.2em] uppercase block">Message</span>{e.message}</p>
                      {e.reference_path && (
                        <p>
                          <span className="text-ink/40 text-[10px] tracking-[0.2em] uppercase block">Reference</span>
                          <a href={fileUrl(e.reference_path)} target="_blank" rel="noreferrer" data-testid={`enquiry-reference-${e.id}`} className="text-alta underline underline-offset-4">
                            {e.reference || "View attachment"}
                          </a>
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-4 md:items-end">
                      <select
                        value={e.status}
                        onChange={(ev) => setStatus(e.id, ev.target.value)}
                        data-testid={`enquiry-status-${e.id}`}
                        className="bg-paper border border-ink/20 px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase outline-none focus:border-alta"
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id}`} className="text-[10px] tracking-[0.2em] uppercase text-ink/40 hover:text-alta transition-colors">
                        Delete enquiry
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}
